import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgRedux } from '@redux/redux-compatibility.service';
import { AppState, actionsList } from 'src/app/shared/redux/store';
import { RevService } from 'src/app/shared/services/rev.service';
import { RevRule, RevDocStatusEnum, RevDoc } from 'src/app/shared/models/rev.model';
import { CatMatch, DataElement, DrawerDetails, ProcessRunStatusResult, ProcessRunStatusEnum } from 'src/app/shared/models/common';
import { SharedService } from 'src/app/shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import Utils from 'src/app/shared/components/utils/utils';
import { Card } from '@shared/models/cat.model';
import { EmbeddedObjectAnnotation } from '@shared/models/embedded-object-annotation';
import { CommonService } from '@shared/services/common.service';
import { ConsistencyIssue, ConsistencyIssueViewModel } from '@shared/components/issues/factory/model/consistency-issue.models';
import { IssuesFactory } from '@shared/components/issues/factory/issues.factory';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ProcessStatusPollingService } from '@shared/services/process-status-polling.service';
import { AuthService } from '@shared/services/auth.service';
import { Config } from 'src/app/config';
import { getObjectPropValue } from '@shared/utils';
import { ReevalJobService } from '@shared/services/reeval-job.service';
import { ReevalJobTarget } from '@shared/models/reeval-job.model';

@Component({
    selector: 'rev-rule-details',
    templateUrl: './rev-rule-details.component.html',
    styleUrls: ['./rev-rule-details.component.scss'],
    standalone: false
})
export class RevRuleDetailsComponent implements OnInit, OnDestroy {
  @Input() focusOnOpen: boolean = false;
  @Input() card: Card | undefined;
  @Input() hasNext: boolean = false;
  @Input() hasPrev: boolean = false;
  @Output() onClose = new EventEmitter();
  @Output() onStatusChange = new EventEmitter();
  @Output() showNextRuleEmitter = new EventEmitter();
  @Output() showPrevRuleEmitter = new EventEmitter();
  @Output() hideRuleAnnotation = new EventEmitter();
  
  loading: boolean;
  ruleStatusOptions: DataElement[] = [];
  dirtySw: boolean;
  formatMatchDifferences: any;
  formatMatchPhrase: any;
  formatMatchEntry: any;
  highlightedIds: number[] = [];
  showIssuesOnFullMatchEnabled: boolean;
  currentIndex: number = 0;
  hideClaimConsistenciesTable: boolean = true;
  showConsistencyIssuePhrases = false;
  currEmbeddedObjectAnnotation: EmbeddedObjectAnnotation;
  drawerDetails: DrawerDetails;
  detailsDialogLabel: string;
  destroy$ = new Subject();
  currentFigureCategory: string;
  initialFigureCategory: string;
  imageId: number;
  imageDocId: number;
  dirtyConsistencyCheckResultsSw = false;
  disableUserActions = false;
  get curRule(): RevRule {
    return this.ngRedux.getState().curRevRule;
  }

  get curDoc(): RevDoc {
    return this.ngRedux.getState().curRevDoc;
  }

  get editable(): boolean {
    return (
      this.ngRedux.getState().curRevDoc.statusCode == RevDocStatusEnum.UNDERWAY && !this.ngRedux.getState().curRevDoc.isProcessRunning
    );
  }

  get isPhrase(): boolean {
    return !this.curRule.isImage;
  }

  get isImage(): boolean {
    return this.curRule.isImage;
  }

  get isPotentialNew(): boolean {
    return this.curRule.revRuleSimilarityEvent == 'POTENTIAL_NEW' 
  }

  get grammarSpellingIssues() {
    if (!this.showIssuesOnFullMatchEnabled && this.curRule.revRuleSimilarityEvent == 'FULL_MATCH') {
      return [];
    }
    return this.curRule.grammarSpellingIssues;
  }

  get referenceIssues() {
    if (!this.showIssuesOnFullMatchEnabled && this.curRule.revRuleSimilarityEvent == 'FULL_MATCH') {
      return [];
    }
    return this.curRule.referenceIssues;
  }

  get isExpanded(): boolean {
    return this.ngRedux.getState().revDocDetailsExpanded;
  }
  set isExpanded(isExpanded) {

    this.ngRedux.getState().revDocDetailsExpanded = isExpanded;
  }

  constructor(
    private ngRedux: NgRedux<AppState>,
    private revService: RevService,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private issueFactory: IssuesFactory,
    private commonService: CommonService,
    private authService: AuthService,
    private processStatusPollingService: ProcessStatusPollingService,
    private reevalJobService: ReevalJobService
  ) {
    this.showIssuesOnFullMatchEnabled = this.sharedService.isShowIssuesOnFullMatchEnabled();
  }
  ngOnDestroy(): void {    
    this.hideRuleAnnotation.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    if (!this.curRule.fullyFetched) {
      this.loading = true;
      this.revService.getRevRuleSingle(this.ngRedux.getState().curRevDoc.docId, this.curRule.imageId, true, false, this.curRule).pipe(takeUntil(this.destroy$))
        .subscribe(
          revRule => {
            this.loading = false;
            let saveSubject = this.curRule.propagateActionFromParent;
            let parentPhraseId = this.curRule.parentPhraseId;
            let isParentActionSelected = this.curRule.isParentActionSelected;
            Object.assign(this.curRule, revRule);
            this.curRule.propagateActionFromParent = saveSubject;
            this.curRule.parentPhraseId = parentPhraseId;
            this.curRule.isParentActionSelected = isParentActionSelected;
            this.initialize();
          },
          error => {
            this.loading = false;
            this.sharedService.showInfoMessage({
              messageKey: 'translate.alert.searchError',
              infoType: 'error'}
            );
          }
        );
    }

    this.initialize();
    this.initFigureCategory();
  }

  private initFigureCategory(): void {
    if (this.curRule && this.curRule.isImage) {
      this.imageId = this.curRule.imageId;
      this.imageDocId = this.curDoc.docId;
      this.initialFigureCategory = this.curRule.figureCategory;
      this.currentFigureCategory = this.curRule.figureCategory;
    }
  }

  get reevalTarget(): ReevalJobTarget {
    return this.curRule && this.curDoc
      ? {
          kind: 'REV',
          docId: this.curDoc.docId,
          elementId: this.curRule.isImage ? this.curRule.imageId : this.curRule.phraseId,
          isImage: this.curRule.isImage
        }
      : null;
  }

  /**
   * Loads the data of a finished background re-evaluation into this dialog and its tile. Unsaved
   * edits are discarded first, after the user confirms.
   */
  onReevalRefresh(): void {
    this.reloadAfterReeval();
  }

  private reloadAfterReeval(): void {
    const hasLocalFigureCategoryEdit = this.isAltDirty();
    this.loading = true;
    this.reevalJobService.refreshTile(this.reevalTarget, true).subscribe({
      complete: () => {
        if (this.destroy$.isStopped) {
          return;
        }
        this.loading = false;
        this.initialize(false);
        if (!hasLocalFigureCategoryEdit) {
          this.initFigureCategory();
        }
      }
    });
  }
    
    checkSaveChangesVisibility(): boolean {
      return (this.imageId >= 0 && this.isAltDirty()) || this.dirtySw || this.dirtyConsistencyCheckResultsSw;
    }
  
    isAltDirty(): boolean {
      return this.currentFigureCategory !== this.initialFigureCategory;
    }
  
    onSaveChangesClicked(): void {
      if (!this.isAltDirty()) {
        
        this.saveConsistencyIssuesChanges();
        return;
      }
  
      this.loading = true;
  
      this.commonService
        .updateImageCategory(
          this.imageDocId,
          this.imageId,
          this.currentFigureCategory,
          true
        )
        .subscribe({
          next: () => {
            this.initialFigureCategory = this.currentFigureCategory;
            this.loading = false;
            this.curRule.figureCategory = this.currentFigureCategory;
            this.saveConsistencyIssuesChanges();
            this.onClose.emit();
          },
          error: err => {
            console.error(err);
            this.currentFigureCategory = this.initialFigureCategory;
            this.loading = false;
            this.saveConsistencyIssuesChanges();
            this.onClose.emit();
          }
        });
    }
  

  initialize(saveOriginal: boolean = true) {
    // populate concatSw from text to format words before further processing
    this.formatMatchEntry = this.curRule.formatMatchEntry;
    if (this.curRule.ruleDispWords.length != this.formatMatchEntry.length) {
      if (this.card && this.card.cardId) {
        this.formatMatchEntry = this.formatMatchEntry.filter(entry => entry.mCardId === this.card.cardId);
        const allFormatDescrEmpty = this.formatMatchEntry.every(entry => !entry.formatDescr || entry.formatDescr.trim() === '');
        if (allFormatDescrEmpty) {
          this.formatMatchEntry = [];
        }
      }
      else {
        this.formatMatchEntry = this._getUniqueEntries(this.curRule.formatMatchEntry);
      }
    }

    this.formatMatchPhrase = this.curRule.formatMatchPhrase;
    if (this.curRule.ruleDispWords.length != this.formatMatchPhrase.length) {
      if (this.card && this.card.cardId) {
        this.formatMatchPhrase = this.formatMatchPhrase.filter(entry => entry.mCardId === this.card.cardId);
      }
      else {
        this.formatMatchPhrase = this._getUniqueEntries(this.curRule.formatMatchPhrase);
      }
    }

    this._populateConcatSw(this.formatMatchEntry);
    this._populateConcatSw(this.formatMatchPhrase);

    this.formatMatchDifferences = this.compareTextObjects(this.formatMatchEntry, this.formatMatchPhrase);
    if (saveOriginal) {
      this.curRule.saveOriginal();
    }

    let actions = [];
    if (this.curRule) {
      actions = this.curRule.revRuleActions.map(
        a => (a == 'APPLY_CHANGE') ? 'MARKED_FOR_CHANGES' : a
      );
    }
    // show all actions on old docs
    let isNewRuleDoc = !!this.curRule.revRuleSimilarityEvent;
    if (isNewRuleDoc) {
      this.ruleStatusOptions =
      this.sharedService.getRevRuleUpdStatusOptions().filter(
        s => {
          return actions.indexOf(s.id) >= 0;
        }
      );
    } else {
      this.ruleStatusOptions = this.sharedService.getRevRuleUpdStatusOptions();
    }
    const uiConfig = this.ngRedux.getState().uiConfig;
    if (uiConfig) {

      const user_action_buttons_enabled_when_dirty = getObjectPropValue(uiConfig, 'rule_details_config.user_action_buttons_enabled_when_dirty');
      if (user_action_buttons_enabled_when_dirty != undefined) {

        this.disableUserActions = !user_action_buttons_enabled_when_dirty;
      }
    }
  }

  _populateConcatSw(entries) {
    entries.map((entry, ind) => entry.concatSw = this.curRule.ruleDispWords[ind].concatSw);
  }

  _getUniqueEntries(entries: any[]): any[] {
    const uniqueMap = new Map();
    for (const entry of entries) {
      if (!uniqueMap.has(entry.id)) {
        uniqueMap.set(entry.id, entry);
      }
    }
    return Array.from(uniqueMap.values());
  }

  close() {
    if (this.dirtySw || this.dirtyConsistencyCheckResultsSw) {
      let validationStr = this.translateService.instant(
        'translate.cat.discardEditChanges'
      );

      this.sharedService.emitPopupchReceivedEvent({
        title: 'translate.validation.confirm',
        icon: 'exclamation-triangle',
        subTitle: validationStr,
        keep: false,
        approve: () => {
          this.discardChanges();
          this.onClose.emit();
        },
      });
    } else {
      this.onClose.emit();
    }
  }

  onRevStatusChange() {
    this.onStatusChange.emit();
  }
  saveConsistencyIssuesChanges() {
    if (this.commonService.hasIssuesToUpdate(this.curRule.consistencyIssuesToUpdate)) {
        this.loading = true;
        // Captured now: on the image-category path the dialog closes before the run number arrives.
        const target = this.reevalTarget;
        const consistencyIssuesSaved$: Observable<unknown> = this.reevalJobService.isAsyncEnabled()
          ? this.commonService.startConsistencyIssuesRun(this.curRule.consistencyIssuesToUpdate)
              .pipe(tap((runNum: number) => this.reevalJobService.startJob(runNum, target)))
          : this.commonService.startConsistencyIssuesRun(this.curRule.consistencyIssuesToUpdate)
              .pipe(switchMap((runNum: number) => this.processStatusPollingService.pollUntilDone(runNum, { until$: this.destroy$ })));
        consistencyIssuesSaved$.subscribe({
            next: () => this.saveRuleChanges(),
            error: () => this.saveRuleChanges()
          });
    } else {
      // comment
      
      if (this.dirtySw || this.dirtyConsistencyCheckResultsSw) {
        
        this.saveRuleChanges();
      }
    }
  }
  saveRuleChanges() {
    this.loading = true;
    
    if (this.curRule.ruleUserStatus || this.curRule.comment){
      this.revService
        .updRevRule(
          this.ngRedux.getState().curRevDoc,
          [this.curRule],
          this.curRule.revStatusCode,
          this.curRule.contextCatPhraseId
        )
        .subscribe(
          (data) => {
            this.loading = false;
            this.dirtySw = false;
            this.dirtyConsistencyCheckResultsSw = false;
            this.ngRedux.dispatch({
                type: actionsList.RELOAD_CAT_DOC_PAGE_REQ,
                data: false
              });
            this.onClose.emit();
          },
          (err) => {
            this.loading = false;
          }
        );
      } else {
        this.loading = false;
        this.dirtySw = false;
        this.dirtyConsistencyCheckResultsSw = false;
        this.ngRedux.dispatch({
                type: actionsList.RELOAD_CAT_DOC_PAGE_REQ,
                data: false
              });
        this.onClose.emit();
      }
  }

  discardChanges() {
    this.curRule.restoreOriginal();

    let consistencyIssues: ConsistencyIssue[] = [];
    this.curRule.consistencyIssues?.forEach((consistencyIssue: ConsistencyIssue) => {
        
      consistencyIssues.push(this.issueFactory.createConsistencyIssue(consistencyIssue, consistencyIssue['embeddedObjectAnnotation'], true));
    });
    this.curRule.consistencyIssues = consistencyIssues;
    this.dirtySw = false;
    this.dirtyConsistencyCheckResultsSw = false;
    this.currentFigureCategory = this.initialFigureCategory;
  }

  onReportDisplaySwChange(match: CatMatch) {
    this.curRule.ruleCatMatches
      .filter((m) => m.catMatchSeq != match.catMatchSeq)
      .forEach((r) => {
        r.reportDisplaySw = false;
      });
    //match.reportDisplaySw = true;
    this.saveConsistencyIssuesChanges();
  }

  compareTextObjects(original, modified): any {
    // original = this.phrase;
    // modified = this.match;
    const differences = [];
    let previousDiff = null;

    original.forEach((originalItem, index) => {
      const diffJson = {
        text: null,
        ids: []
      };
      const modifiedItem = modified[index];
      if (originalItem.formatDescr !== modifiedItem.formatDescr) {
        diffJson.text = originalItem.text;
        diffJson.ids.push(originalItem.id);

        const originalFormat = originalItem.formatDescr.split(', ');
        const modifiedFormat = modifiedItem.formatDescr.split(', ');

        originalFormat.forEach((originalAttr, attrIndex) => {
          const [originalAttrKey, originalAttrVal] = originalAttr.split(':');
          const [modifiedAttrKey, modifiedAttrVal] = modifiedFormat[attrIndex].split(':');

          if (originalAttrVal !== modifiedAttrVal) {
            diffJson[originalAttrKey] = [originalAttrVal, modifiedAttrVal];
          }
        });
        if (this.isSameDifference(diffJson, previousDiff)) {
          differences[differences.length - 1].text += ' ' + diffJson.text;
          differences[differences.length - 1].ids.push(diffJson.ids[0]);
        } else {
          differences.push(diffJson);
        }
        previousDiff = diffJson;
      }
    });

    return differences;
  }

  getFriendlyDifferenceName(text: string): string {
    return text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  getFriendlyFontName(text: string): string {
    return text.replace(/['{}]/g, '');
  }

  isHighlighted(ids: number[]): boolean {
    return this.highlightedIds.some(id => ids.includes(id));
  }

  highlightDiff(ids: number[]): void {
    this.highlightedIds = ids;
  }

  clearHighlight(): void {
    this.highlightedIds = [];
  }

  isSameDifference(diff1, diff2): boolean {
    const stripText = ({ text, ids, ...obj }: any) => obj;

    const strippedDiff1 = stripText(diff1);
    if (diff2) {
      const strippedDiff2 = stripText(diff2);
      return Utils.areEqual(strippedDiff1, strippedDiff2);
    } else {
      return false;
    }
  }

  getMatchStatusDescr(): string {
  
    if(this.card) {
      const revCardMatches = this.ngRedux.getState().revCardMatches;
      const match = revCardMatches.find((match: any) => match.phraseId === this.curRule.phraseId && match.mCardId === this.card.cardId);
      return match ? match.matchStatusDescr : this.curRule.revStatusDescr;
    }
  
    return this.curRule.revStatusDescr;
  }

  getMatchStatusCode(): string {
    /*
    let code = this.curRule.revStatusCode;
    console.log(code);
    console.log("code");
    if(this.card) {
      const revCardMatches = this.ngRedux.getState().revCardMatches;
      const match = revCardMatches.find((match: any) => match.phraseId === this.curRule.phraseId && match.mCardId === this.card.cardId);
      code = match ? match.matchStatusCode : this.curRule.revStatusCode;
    }
    return code == 'FULL_MATCH' ? 'PASSED' : 'NOT_PASSED_NEW_CONTENT';
    */
  return this.curRule.revStatusCode;
  }
  showPrevRule() {
    this.showPrevRuleEmitter.emit();
  }

  showNextRule() {
    this.showNextRuleEmitter.emit();
  }

  toggle() {
    if (this.isExpanded) {
      this.drawerDetails = null;
    }
    this.ngRedux.getState().revDocDetailsExpanded = !this.ngRedux.getState().revDocDetailsExpanded;
  }
  get detailsDialogClass(): string {
    if (this.isExpanded) {
      return 'expanded';
    } else {
      return 'collapsed';
    }
  }

  get imageClassName() {
    
    return 'small-image';
  
  }

  viewConsistencyIssue(consistencyIssueViewModel: ConsistencyIssueViewModel) {

    switch(consistencyIssueViewModel.type) {
      case 'reference_consistency_issue': 
        this.currEmbeddedObjectAnnotation = consistencyIssueViewModel.data;
        this.showConsistencyIssuePhrases = true;
        break;
      case 'claim_image_consistency_issue': 
        this.drawerDetails = consistencyIssueViewModel.data;
        this.detailsDialogLabel = 'translate.consistencyIssue.details';
        this.isExpanded = true;
        break;
      case 'cat_image_consistency_issue': 
        this.drawerDetails = consistencyIssueViewModel.data?.drawerDetails;
        let imageId = consistencyIssueViewModel.data?.idToCheck;
        this.detailsDialogLabel = 'translate.consistencyIssue.details';
        let correspondingIndex = this.curRule.ruleCatMatches.findIndex(catMatch => catMatch.catImage.imageId == imageId);
        if (correspondingIndex != -1) {
          this.currentIndex = correspondingIndex;
        }
        this.isExpanded = true;
        break;
    }

  }

  removeIssueEvent() {
    
    this.dirtyConsistencyCheckResultsSw = true; 
    
  }

  getCurrentMatch(): CatMatch {
    return this.curRule.ruleCatMatches[this.currentIndex];
  }

  prevMatch() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.curRule.ruleCatMatches.length - 1;
    }
  }

  nextMatch() {
    if (this.currentIndex < this.curRule.ruleCatMatches.length - 1) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;
    }
  }
  get imageCategoriesForDoc() {
    return this.ngRedux.getState().imageCategoriesForDoc;
  }
  checkComment() {
    if (this.curRule.comment) {
      this.dirtySw = true;
    }
  }
  get conditionForDisablingConsistencyCheckResultActions() {

    return this.disableUserActions && this.curRule.disableConsistencyCheckResultsActions;
  }

  get conditionForDisablingRevStatusChanges() {
    
    return this.disableUserActions && this.dirtyConsistencyCheckResultsSw;
  }

}